/**
 * ML / CV / MAS tool service — COPILOT_MASTER_PLAN subsystems N + D (client portal).
 *
 * Thin client wrappers the Companion tool-registry (subsystem C) calls to invoke
 * the LIVE inference + autonomy backends as first-class tools. NO new backend
 * endpoints — every call rides an existing route:
 *
 *   • ML   POST /api/v1/ai/ml/<model>        (ai-orchestrator ml_pipeline, remote-first)
 *   • CV   POST /api/v2/analyze  (multipart, model=<name>)  + POST /api/v2/ingest
 *   • MAS  POST /api/v1/agentic/mas/plans[/{id}/start|/progress]  (subsystem P)
 *   • NLQ  POST /api/v1/nl-query/execute      (queryData fallback until data-QA lands)
 *   • Notif POST /api/v1/notifications        (in-app + email; WS delivery when wired)
 *
 * Every function returns a normalised, provenance-tagged result so the Companion
 * can render an <MlResultCard> with `source` + `credit`. Missing endpoints degrade
 * to `{ ok:false, missing:true }` rather than throwing, so a tool proposal on a
 * page whose model isn't deployed fails soft (the agent-runner skips read fails).
 */

import { post, get } from '@/lib/api/client'
import { getTenantId, getUserId } from '@/lib/auth/keycloak'

// ── Shared helpers ────────────────────────────────────────────────────────────

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status
}

/** 404/405/501 = endpoint not deployed here → the tool reports `missing`. */
export function isMissingEndpoint(err: unknown): boolean {
  const s = httpStatus(err)
  return s === 404 || s === 405 || s === 501
}

function errMessage(err: unknown): string {
  const data = (err as { response?: { data?: { detail?: string; message?: string } } })?.response?.data
  return data?.detail ?? data?.message ?? (err instanceof Error ? err.message : 'Erreur inconnue')
}

// ── ML inference (subsystem N) ────────────────────────────────────────────────

export interface MlToolResult {
  ok: boolean
  /** Normalised scalar result rows (the innermost prediction object). */
  result: Record<string, unknown>
  /** Full raw payload (for the detail view). */
  raw?: unknown
  /** Provenance — 'ml-inference' when the pipeline pinned a real model, else 'ml:<type>'. */
  source: string
  /** Credits consumed by this call (1 credit = $0.01), when the backend reports it. */
  credit?: number | null
  /** True when the endpoint is not deployed here (fail-soft). */
  missing?: boolean
  error?: string
}

/**
 * Pull the innermost result object from an ML pipeline response. The pipeline
 * nests under a per-model key (prediction / triage / drift / early_warning / …);
 * fall back to the first nested object.
 */
function unwrapMlResult(payload: unknown, preferredKeys: string[]): Record<string, unknown> {
  if (!payload || typeof payload !== 'object') return {}
  const obj = payload as Record<string, unknown>
  for (const k of preferredKeys) {
    const v = obj[k]
    if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>
  }
  const firstObj = Object.entries(obj).find(
    ([k, v]) => k !== 'model' && v && typeof v === 'object' && !Array.isArray(v),
  )
  if (firstObj) return firstObj[1] as Record<string, unknown>
  // No nested object → surface the top-level scalars (some models return flat).
  const flat: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || typeof v !== 'object') flat[k] = v
  }
  return flat
}

function mlProvenance(payload: unknown): { source: string; credit: number | null } {
  const obj = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>
  const meta = obj.model as Record<string, unknown> | undefined
  const source =
    typeof obj.source === 'string'
      ? obj.source
      : meta && typeof meta === 'object' && typeof meta.type === 'string'
        ? `ml:${meta.type}`
        : 'ml-inference'
  const credit =
    typeof obj.credit === 'number'
      ? obj.credit
      : typeof obj.credits === 'number'
        ? (obj.credits as number)
        : typeof obj.credits_used === 'number'
          ? (obj.credits_used as number)
          : null
  return { source, credit }
}

/**
 * Invoke one ML predictor. `resultKeys` are the per-model nesting keys tried in
 * order (e.g. ['prediction'], ['triage'], ['drift']). Never throws.
 */
export async function runMlModel(
  endpoint: string,
  payload: Record<string, unknown>,
  resultKeys: string[] = ['prediction', 'result'],
): Promise<MlToolResult> {
  try {
    const raw = await post<unknown>(endpoint, payload, { timeout: 120_000 })
    const { source, credit } = mlProvenance(raw)
    return { ok: true, result: unwrapMlResult(raw, resultKeys), raw, source, credit }
  } catch (err) {
    if (isMissingEndpoint(err)) {
      return { ok: false, result: {}, source: 'ml-inference', missing: true, error: 'Modèle non déployé ici' }
    }
    return { ok: false, result: {}, source: 'ml-inference', error: errMessage(err) }
  }
}

// ── CV / document-AI (subsystem N — perception chain) ─────────────────────────

export interface CvAnalyzeResult extends MlToolResult {
  /** Extracted text (OCR), when the model produced it — feeds fillForm/import. */
  documentText?: string
  /** Extracted structured fields, when the model produced them. */
  fields?: Record<string, unknown>
}

/**
 * Run a single CV model over a file (OCR / classification / extraction) via the
 * live `POST /api/v2/analyze` (multipart, `model=<name>`). `persist=false` keeps
 * it ephemeral (no ingestion). Returns extracted text + fields when present.
 */
export async function runCvAnalyze(file: File, model: string): Promise<CvAnalyzeResult> {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('model', model)
  try {
    const raw = await post<Record<string, unknown>>('/api/v2/analyze', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180_000,
    })
    const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
    const analysis = (obj.analysis ?? obj.result ?? obj) as Record<string, unknown>
    const documentText =
      (typeof obj.text === 'string' && obj.text) ||
      (typeof analysis.text === 'string' && analysis.text) ||
      (typeof obj.document_text === 'string' && obj.document_text) ||
      undefined
    const fields =
      (analysis.fields && typeof analysis.fields === 'object'
        ? (analysis.fields as Record<string, unknown>)
        : undefined) ??
      (obj.fields && typeof obj.fields === 'object' ? (obj.fields as Record<string, unknown>) : undefined)
    return {
      ok: true,
      result: unwrapMlResult(analysis, ['classification', 'result', 'prediction']),
      raw,
      source: typeof obj.source === 'string' ? obj.source : 'ml-inference',
      credit: typeof obj.credit === 'number' ? obj.credit : null,
      documentText: documentText || undefined,
      fields,
    }
  } catch (err) {
    if (isMissingEndpoint(err)) {
      return { ok: false, result: {}, source: 'ml-inference', missing: true, error: 'CV non déployé ici' }
    }
    return { ok: false, result: {}, source: 'ml-inference', error: errMessage(err) }
  }
}

// ── MAS autonomy (subsystem D) ────────────────────────────────────────────────

export interface MasCreatePlanResult {
  ok: boolean
  planId?: string
  status?: string
  strategy?: string
  model?: string
  subtaskCount?: number
  missing?: boolean
  error?: string
}

/**
 * Create a MAS plan (durable, multi-agent) from a natural-language goal. When
 * `autoStart` is true the dispatcher runs it in the background immediately;
 * otherwise the caller starts it explicitly via `startMasPlan`. Tenant/user are
 * JWT-derived server-side — we pass the goal + optional domain + autonomy.
 */
export async function createMasPlan(input: {
  goal: string
  domain?: string
  context?: Record<string, unknown>
  autonomy?: 'assistant' | 'agent'
  autoStart?: boolean
  maxSubtasks?: number
}): Promise<MasCreatePlanResult> {
  try {
    const raw = await post<Record<string, unknown>>(
      '/agentic/mas/plans',
      {
        goal: input.goal,
        domain: input.domain,
        context: input.context ?? {},
        autonomy: input.autonomy ?? 'agent',
        auto_start: input.autoStart ?? false,
        max_subtasks: Math.min(16, Math.max(2, input.maxSubtasks ?? 8)),
      },
      { timeout: 120_000 },
    )
    const planId = String(raw?.plan_id ?? raw?.id ?? '')
    const subtasks = Array.isArray(raw?.subtasks) ? (raw.subtasks as unknown[]) : []
    if (!planId) return { ok: false, error: 'MAS n’a pas renvoyé d’identifiant de plan' }
    return {
      ok: true,
      planId,
      status: typeof raw?.status === 'string' ? raw.status : 'READY',
      strategy: typeof raw?.strategy === 'string' ? raw.strategy : undefined,
      model: typeof raw?.model === 'string' ? raw.model : undefined,
      subtaskCount: subtasks.length,
    }
  } catch (err) {
    if (isMissingEndpoint(err)) return { ok: false, missing: true, error: 'MAS non déployé' }
    return { ok: false, error: errMessage(err) }
  }
}

/** Dispatch an existing MAS plan in the background. */
export async function startMasPlan(planId: string): Promise<{ ok: boolean; status?: string; error?: string }> {
  try {
    const raw = await post<Record<string, unknown>>(`/agentic/mas/plans/${planId}/start`, {})
    return { ok: true, status: typeof raw?.status === 'string' ? raw.status : 'RUNNING' }
  } catch (err) {
    return { ok: false, error: errMessage(err) }
  }
}

export type MasControl = 'pause' | 'resume' | 'cancel'

export async function controlMasPlan(planId: string, action: MasControl): Promise<{ ok: boolean; status?: string; error?: string }> {
  try {
    const raw = await post<Record<string, unknown>>(`/agentic/mas/plans/${planId}/${action}`, {})
    return { ok: true, status: typeof raw?.status === 'string' ? raw.status : undefined }
  } catch (err) {
    return { ok: false, error: errMessage(err) }
  }
}

export interface MasProgress {
  planId: string
  status: string
  progressPct: number
  creditsUsed: number
  counts: Record<string, number>
  subtasks: {
    key: string
    title: string
    status: string
    assignee?: string
    priority?: number | null
    progressPct?: number | null
  }[]
}

/** Poll aggregate progress for a running MAS plan. Null when unavailable. */
export async function getMasProgress(planId: string): Promise<MasProgress | null> {
  try {
    const raw = await get<Record<string, unknown>>(`/agentic/mas/plans/${planId}/progress`)
    const subRows = Array.isArray(raw?.subtasks) ? (raw.subtasks as Record<string, unknown>[]) : []
    return {
      planId,
      status: String(raw?.status ?? 'RUNNING'),
      progressPct: typeof raw?.progress_pct === 'number' ? raw.progress_pct : 0,
      creditsUsed: typeof raw?.credits_used === 'number' ? raw.credits_used : Number(raw?.credits_used ?? 0) || 0,
      counts: (raw?.counts && typeof raw.counts === 'object' ? raw.counts : {}) as Record<string, number>,
      subtasks: subRows.map((s) => ({
        key: String(s.key ?? ''),
        title: String(s.title ?? s.key ?? 'Sous-tâche'),
        status: String(s.status ?? 'PENDING'),
        assignee: typeof s.assignee === 'string' ? s.assignee : undefined,
        priority: typeof s.priority === 'number' ? s.priority : null,
        progressPct: typeof s.progress_pct === 'number' ? s.progress_pct : null,
      })),
    }
  } catch {
    return null
  }
}

// ── queryData → NLQ (subsystem C; data-QA/feature-detect lands later) ─────────

export interface QueryDataResult {
  ok: boolean
  summary: string
  rows: Record<string, unknown>[]
  chart?: string
  confidence?: number
  source: 'data-qa' | 'nl-query' | 'none'
  error?: string
}

/**
 * Candidate data-QA endpoints (S-agent's feature-detect lands here), then the
 * LIVE NL-query agent as the guaranteed fallback. First usable shape wins and is
 * cached for the session.
 */
const DATA_QA_CANDIDATES = ['/data-qa/query', '/ai/data-qa/query', '/governance-pro/data-qa/query']
let dataQaPath: string | null | 'unknown' = 'unknown'

function normalizeQueryPayload(raw: unknown, source: QueryDataResult['source']): QueryDataResult {
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const data = Array.isArray(obj.data)
    ? (obj.data as Record<string, unknown>[])
    : Array.isArray(obj.rows)
      ? (obj.rows as Record<string, unknown>[])
      : Array.isArray(obj.results)
        ? (obj.results as Record<string, unknown>[])
        : []
  return {
    ok: true,
    summary: String(obj.summary ?? obj.answer ?? obj.interpreted_query ?? ''),
    rows: data,
    chart: typeof obj.suggested_chart === 'string' ? obj.suggested_chart : (typeof obj.chart === 'string' ? obj.chart : undefined),
    confidence: typeof obj.confidence === 'number' ? obj.confidence : undefined,
    source,
  }
}

// Whether the richer data-QA / NL-query surfaces are worth probing. They are NOT
// routed at the current gateway, and probing them on every call floods the
// console with 404s (violating the zero-console-error bar). We therefore probe
// the candidate set AT MOST ONCE per session; once all miss, we go straight to
// the always-live knowledge search and never probe the dead paths again.
let richQueryProbed = false

async function tryRichQuery(question: string): Promise<QueryDataResult | null> {
  if (richQueryProbed) return dataQaPath && dataQaPath !== 'unknown' && dataQaPath !== null
    ? tryDataQaPath(dataQaPath, question)
    : null

  richQueryProbed = true
  // data-QA feature-detect (S-agent lands these later).
  for (const path of DATA_QA_CANDIDATES) {
    const r = await tryDataQaPath(path, question)
    if (r) {
      dataQaPath = path
      return r
    }
  }
  dataQaPath = null
  // NL-query agent (when the gateway routes it).
  try {
    const raw = await post<unknown>(
      '/nl-query/execute',
      { query: question, tenant_id: getTenantId() ?? '', user_id: getUserId() ?? '' },
      { timeout: 90_000 },
    )
    return normalizeQueryPayload(raw, 'nl-query')
  } catch {
    return null
  }
}

async function tryDataQaPath(path: string, question: string): Promise<QueryDataResult | null> {
  try {
    const raw = await post<unknown>(path, { question, query: question }, { timeout: 90_000 })
    return normalizeQueryPayload(raw, 'data-qa')
  } catch {
    return null
  }
}

/**
 * Answer a data question over the tenant's records. Uses the always-live
 * knowledge semantic search as the guaranteed source, and — best-effort, once
 * per session — the richer data-QA / NL-query surfaces when they are routed.
 * Never throws.
 */
export async function queryData(question: string): Promise<QueryDataResult> {
  // Guaranteed, always-live source: knowledge semantic search (real tenant
  // records). This answers today with zero probe-404 noise.
  try {
    const raw = await post<unknown>(
      '/knowledge/entities/search',
      { query: question, limit: 20 },
      { timeout: 60_000 },
    )
    const obj = raw as { hits?: unknown[]; results?: unknown[] } | unknown[]
    const hits = Array.isArray(obj) ? obj : (obj?.hits ?? obj?.results ?? [])
    const rows = (hits as Record<string, unknown>[]).map((h) => ({
      titre: h.title ?? h.entity_id,
      type: h.entity_type,
      score: typeof h.score === 'number' ? Number(h.score.toFixed(3)) : h.score,
    }))
    // If the richer surfaces are already known-live, prefer their structured
    // answer; otherwise the knowledge rows ARE the answer.
    if (dataQaPath && dataQaPath !== 'unknown' && dataQaPath !== null) {
      const rich = await tryDataQaPath(dataQaPath, question)
      if (rich) return rich
    }
    return {
      ok: true,
      summary: rows.length ? `${rows.length} enregistrement(s) pertinent(s)` : 'Aucun résultat',
      rows,
      source: 'nl-query',
    }
  } catch (err) {
    // Knowledge search itself failed → fall back to the richer surfaces once.
    const rich = await tryRichQuery(question)
    if (rich) return rich
    return { ok: false, summary: '', rows: [], source: 'none', error: errMessage(err) }
  }
}

// ── notify (subsystem C/E) ────────────────────────────────────────────────────

/**
 * Send an in-app notification (WS/SSE when the realtime endpoint is wired, email
 * always). Best-effort — returns `false` when the notification service is down.
 */
export async function sendNotification(input: {
  title: string
  body: string
  channels?: string[]
  priority?: string
}): Promise<{ ok: boolean; error?: string }> {
  const userId = getUserId()
  try {
    await post('/notifications', {
      recipient: userId ?? undefined,
      channels: input.channels ?? ['WEBSOCKET', 'EMAIL'],
      notification_type: 'copilot',
      priority: input.priority ?? 'normal',
      content: { title: input.title, body: input.body },
      idempotency_key: `copilot:${Date.now()}:${Math.random().toString(36).slice(2)}`,
      metadata: { source: 'copilot' },
    })
    return { ok: true }
  } catch (err) {
    return { ok: false, error: errMessage(err) }
  }
}
